import React from 'react';
import { 
  ArrowLeft, 
  Calendar, 
  Clock, 
  MapPin, 
  Users, 
  Tag, 
  CalendarDays,
  CheckCircle,
  XCircle,
  Clock4,
  Archive
} from 'lucide-react';
import { Event, getEffectiveEventStatus } from '../../types/event';
import Button from '../UI/Button';
import { useAppSelector } from '../../store/hooks';

interface EventPageHeaderProps {
  event: Event;
  onBack: () => void;
  guestStats?: {
    total_invited: number;
    total_attendees: number;
    response_rate: number;
    status_breakdown: {
      pending: number;
      accepted: number;
      rejected: number;
      ignored: number;
    };
  };
}

const EventPageHeader: React.FC<EventPageHeaderProps> = ({
  event,
  onBack,
  guestStats
}) => {
  const { theme } = useAppSelector((state) => state.ui);
  const effectiveStatus = getEffectiveEventStatus(event);

  const getStatusBadge = () => {
    // Check if event has ended - if status is 'active' but event date has passed, show 'Ended'
    const eventHasEnded = isPast() && effectiveStatus === 'active';
    
    const statusConfig = {
      pending: {
        label: 'Pending Approval',
        icon: Clock4,
        className: theme === 'dark' ? 'bg-orange-900/30 text-orange-300 border border-orange-700/50' : 'bg-orange-100 text-orange-800',
        iconClassName: theme === 'dark' ? 'text-orange-300' : 'text-orange-800'
      },
      active: {
        label: eventHasEnded ? 'Ended' : 'Active',
        icon: CheckCircle,
        className: eventHasEnded
          ? (theme === 'dark' ? 'bg-gray-800/50 text-gray-300 border border-gray-600/50' : 'bg-gray-100 text-gray-800')
          : (theme === 'dark' ? 'bg-[#33FF00]/20 text-[#33FF00] border border-[#33FF00]/30' : 'bg-mirsal-green text-mirsal-black'),
        iconClassName: eventHasEnded
          ? (theme === 'dark' ? 'text-gray-300' : 'text-gray-800')
          : (theme === 'dark' ? 'text-[#33FF00]' : 'text-black')
      },
      rescheduled: {
        label: 'Rescheduled',
        icon: Calendar,
        className: theme === 'dark' ? 'bg-yellow-900/30 text-yellow-300 border border-yellow-700/50' : 'bg-yellow-100 text-yellow-800',
        iconClassName: theme === 'dark' ? 'text-yellow-300' : 'text-yellow-800'
      },
      completed: {
        label: 'Completed',
        icon: CheckCircle,
        className: theme === 'dark' ? 'bg-blue-900/30 text-blue-300 border border-blue-700/50' : 'bg-blue-100 text-blue-800',
        iconClassName: theme === 'dark' ? 'text-blue-300' : 'text-blue-800'
      },
      canceled: {
        label: 'Cancelled',
        icon: XCircle,
        className: theme === 'dark' ? 'bg-red-900/30 text-red-300 border border-red-700/50' : 'bg-red-100 text-red-800',
        iconClassName: theme === 'dark' ? 'text-red-300' : 'text-red-800'
      },
      rejected: {
        label: 'Rejected',
        icon: XCircle,
        className: theme === 'dark' ? 'bg-red-900/40 text-red-300 border border-red-700/50' : 'bg-red-200 text-red-900',
        iconClassName: theme === 'dark' ? 'text-red-300' : 'text-red-900'
      },
      archived: {
        label: 'Archived',
        icon: Archive,
        className: theme === 'dark' ? 'bg-gray-800/50 text-gray-300 border border-gray-600/50' : 'bg-gray-100 text-gray-800',
        iconClassName: theme === 'dark' ? 'text-gray-300' : 'text-gray-800'
      }
    };

    const config = statusConfig[effectiveStatus];

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.className}`}>
        <span className={`w-1.5 h-1.5 rounded-full mr-1.5 ${
          eventHasEnded ? 'bg-gray-500' :
          effectiveStatus === 'active' ? 'bg-mirsal-green' :
          effectiveStatus === 'pending' ? 'bg-orange-500' :
          effectiveStatus === 'rescheduled' ? 'bg-yellow-500' :
          effectiveStatus === 'completed' ? 'bg-blue-500' :
          effectiveStatus === 'canceled' || effectiveStatus === 'rejected' ? 'bg-red-500' :
          'bg-gray-500'
        }`}></span>
        {config.label}
      </span>
    );
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatTime = (timeString: string) => {
    return new Date(`2000-01-01T${timeString}`).toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  const isUpcoming = () => {
    const eventDate = new Date(`${event.event_date}T${event.event_time}`);
    return eventDate > new Date();
  };

  const isPast = () => {
    const eventDate = new Date(`${event.event_date}T${event.event_time}`);
    return eventDate < new Date();
  };

  const getDaysUntilEvent = () => {
    // Get today's date at midnight (start of day) for accurate day comparison
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    // Get event date at midnight (start of day) - ignore time for day calculation
    const eventDateOnly = new Date(event.event_date + 'T00:00:00');
    
    // Calculate difference in days
    const diffTime = eventDateOnly.getTime() - today.getTime();
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    
    return diffDays;
  };

  const daysUntil = getDaysUntilEvent();

  return (
    <div className={`relative overflow-hidden rounded-2xl shadow-2xl border backdrop-blur-sm mx-4 sm:mx-6 lg:mx-8 mb-4 ${
      theme === 'dark' 
        ? 'bg-black/80 border-gray-800/50' 
        : 'bg-white/90 border-gray-200/50'
    }`}>
      
      {/* Content */}
      <div className="relative z-10 p-5 sm:p-6">
        {/* Navigation and Actions */}
        <div className="flex items-center justify-between mb-6">
          <Button
            variant="secondary"
            onClick={onBack}
            leftIcon={<ArrowLeft className="h-4 w-4" />}
          >
            Back to Events
          </Button>
        </div>

        {/* Main Header Content */}
        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
          {/* Left Side - Event Info */}
          <div className="flex-1 min-w-0">
            {/* Title and Status */}
            <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-4">
              <h1 className={`text-2xl font-bold leading-tight ${
                theme === 'dark' ? 'text-white' : 'text-gray-900'
              }`}>
                {event.title}
              </h1>
              <div className="flex items-center gap-2">
                {getStatusBadge()}
                {event.series_name && (
                   <div className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${
                     theme === 'dark' 
                       ? 'bg-blue-900/30 text-blue-300 border border-blue-700/50' 
                       : 'bg-blue-100 text-blue-800'
                   }`}>
                     <CalendarDays className={`h-3 w-3 mr-1.5 ${
                       theme === 'dark' ? 'text-blue-300' : 'text-blue-800'
                     }`} />
                     Series
                   </div>
                )}
              </div>
            </div>

            {/* Event Meta Information */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
              {/* Date & Time */}
               <div className={`p-3 rounded-lg border ${
                 theme === 'dark' 
                   ? 'bg-gray-800/50 border-gray-700/50' 
                   : 'bg-white border-gray-200'
               }`}>
                 <div className="flex items-center mb-2">
                   <Calendar className={`h-4 w-4 mr-2 ${
                     theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
                   }`} />
                   <span className={`text-xs font-medium ${
                     theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
                   }`}>Date & Time</span>
                 </div>
                 <div>
                   <p className={`text-sm font-medium ${
                     theme === 'dark' ? 'text-white' : 'text-gray-900'
                   }`}>{formatDate(event.event_date)}</p>
                   <p className={`text-xs ${
                     theme === 'dark' ? 'text-gray-400' : 'text-gray-400'
                   }`}>{formatTime(event.event_time)}</p>
                 </div>
               </div>

               {/* Location */}
               <div className={`p-3 rounded-lg border ${
                 theme === 'dark' 
                   ? 'bg-gray-800/50 border-gray-700/50' 
                   : 'bg-white border-gray-200'
               }`}>
                 <div className="flex items-center mb-2">
                   <MapPin className={`h-4 w-4 mr-2 ${
                     theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
                   }`} />
                   <span className={`text-xs font-medium ${
                     theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
                   }`}>Location</span>
                 </div>
                 <div>
                   <p className={`text-sm font-medium ${
                     theme === 'dark' ? 'text-white' : 'text-gray-900'
                   }`} title={event.location}>
                     {event.location}
                   </p>
                 </div>
               </div>

               {/* Host Name - Changed from Guests to show Host */}
               <div className={`p-3 rounded-lg border ${
                 theme === 'dark' 
                   ? 'bg-gray-800/50 border-gray-700/50' 
                   : 'bg-white border-gray-200'
               }`}>
                 <div className="flex items-center mb-2">
                   <Users className={`h-4 w-4 mr-2 ${
                     theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
                   }`} />
                   <span className={`text-xs font-medium ${
                     theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
                   }`}>Host</span>
                 </div>
                 <div>
                   <p className={`text-sm font-medium ${
                     theme === 'dark' ? 'text-white' : 'text-gray-900'
                   }`}>
                     {event.host_name || 
                      (event.host?.get_full_name) || 
                      (event.host?.first_name && event.host?.last_name 
                        ? `${event.host.first_name} ${event.host.last_name}`.trim()
                        : event.host?.email || event.host?.phone || 'Unknown')}
                   </p>
                 </div>
               </div>

               {/* Category */}
               {event.category && (
                 <div className={`p-3 rounded-lg border ${
                   theme === 'dark' 
                     ? 'bg-gray-800/50 border-gray-700/50' 
                     : 'bg-white border-gray-200'
                 }`}>
                   <div className="flex items-center mb-2">
                     <Tag className={`h-4 w-4 mr-2 ${
                       theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
                     }`} />
                     <span className={`text-xs font-medium ${
                       theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
                     }`}>Category</span>
                   </div>
                   <div>
                     <p className={`text-sm font-medium ${
                       theme === 'dark' ? 'text-white' : 'text-gray-900'
                     }`}>{event.category.name}</p>
                   </div>
                 </div>
               )}
            </div>

             {/* Time Indicator */}
             {isUpcoming() && daysUntil >= 0 && (
               <div className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-[#33FF00] to-[#2BD400] text-black rounded-xl text-sm font-semibold shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300 ease-out">
                 <Clock className="h-4 w-4 mr-2" />
                 {daysUntil === 0 ? 'Today' : 
                  daysUntil === 1 ? 'Tomorrow' : 
                  `${daysUntil} days away`}
               </div>
             )}
            {isPast() && (
              <div className={`inline-flex items-center px-4 py-2 rounded-xl text-sm font-semibold shadow-lg ${
                theme === 'dark' 
                  ? 'bg-gradient-to-r from-gray-600 to-gray-700 text-gray-300' 
                  : 'bg-gradient-to-r from-gray-400 to-gray-500 text-white'
              }`}>
                <Clock className="h-4 w-4 mr-2" />
                Event has passed
              </div>
            )}
          </div>
        </div>

         {/* Series Information */}
         {event.series_name && (
           <div className={`mt-6 p-4 border rounded-lg ${
             theme === 'dark' 
               ? 'bg-gray-800/50 border-gray-700/50' 
               : 'bg-white border-gray-200'
           }`}>
             <div className="flex items-center">
               <CalendarDays className={`h-4 w-4 mr-2 ${
                 theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
               }`} />
               <div>
                 <p className={`text-sm font-medium ${
                   theme === 'dark' ? 'text-white' : 'text-gray-900'
                 }`}>
                   Part of "{event.series_name}" series
                 </p>
                 {event.series_position && (
                   <p className={`text-xs ${
                     theme === 'dark' ? 'text-gray-400' : 'text-gray-400'
                   }`}>
                     Event #{event.series_position} in the series
                   </p>
                 )}
               </div>
             </div>
           </div>
         )}

        {/* Rejection reason display removed as requested */}
      </div>
    </div>
  );
};

export default EventPageHeader;
